import React, { type ReactNode } from "react";
import ReactDOM from "react-dom";

import { debounce } from "lodash";
import { Plugin, PluginKey } from "prosemirror-state";
import { EditorView } from "prosemirror-view";

import useToggle from "~/lib/hooks/useToggle";

import { Extension } from "../extension";

const reactHostPluginKey = new PluginKey<ReactHost>("react-host");

export function reactRender(editorView: EditorView, domNode: Element, reactNode?: React.ReactNode, flush = false) {
	const reactHostExt = reactHostPluginKey.getState(editorView.state);
	if (!(reactHostExt instanceof ReactHost)) {
		console.warn("ReactHost plugin is not present");
		return;
	}
	reactHostExt.render(domNode, reactNode, flush);
}

export function flushReactRender(editorView: EditorView) {
	const reactHostExt = reactHostPluginKey.getState(editorView.state);
	if (!(reactHostExt instanceof ReactHost)) {
		console.warn("ReactHost plugin is not present");
		return;
	}
	reactHostExt.notifyContainer();
}

class ReactHostPortalStorage {
	portals: { [k: string]: [Element, React.ReactNode, React.ReactPortal] } = {};
	add = (dom: Element, reactNode: ReactNode) => {
		const id = dom.getAttribute("portal-id") || "";
		if (!id) console.warn("It is required to have a `portal-id` attribute on dom element: ", dom);
		this.portals[id] = [dom, reactNode, ReactDOM.createPortal(reactNode, dom, id)];
	};
	remove = (dom: Element) => {
		const id = dom.getAttribute("portal-id") || "";
		delete this.portals[id];
	};
	update = (dom: Element, reactNode: React.ReactNode) => {
		if (reactNode) {
			this.add(dom, reactNode);
		} else {
			this.remove(dom);
		}
	};
	getContent = () => {
		return Object.values(this.portals).map(([_dom, _reactNode, portal]) => {
			return portal;
		});
	};
}

class ReactHost extends Extension {
	portalStorage = new ReactHostPortalStorage();
	eventCount = 0;
	get name() {
		return "ReactHost";
	}
	_notifyContainer = () => {
		this.dispatchEvent(new CustomEvent("change"));
		this.eventCount += 1;
	};
	notifyContainer = debounce(this._notifyContainer, 100, { leading: true, trailing: true });
	// use this plugin to get a reference to this extension given an editor state.
	// This is more reliable than view.extensions, because view.extensions only get set after the view is initialized.
	get plugins() {
		const ext = this;
		return [
			new Plugin({
				key: reactHostPluginKey,
				state: {
					init: () => ext,
					apply: (_tr, v) => v,
				},
			}),
		];
	}
	render = (domNode: Element, reactNode?: ReactNode, notify = false) => {
		this.portalStorage.update(domNode, reactNode);
		if (notify) {
			this.notifyContainer();
		}
	};
	init() {}
}

export const ReactHostContainer = React.memo(function ReactHostContainer({
	reactHost,
}: {
	reactHost: ReactHost | null;
}) {
	const [_flag, toggleFlag] = useToggle();
	const initialReactHostEventCount = React.useRef(reactHost?.eventCount || 0);
	React.useEffect(() => {
		const listener = () => {
			toggleFlag();
		};
		if (reactHost) {
			reactHost.addEventListener("change", listener);
			// There might be events that are not captured because the listener hasn't being added yet.
			// Trigger listener manually in this case.
			// (We can only do this because we only have one type of event, other wise we need a queue)
			if (reactHost.eventCount > initialReactHostEventCount.current) {
				listener();
			}
		}
		return () => {
			if (reactHost) {
				reactHost.removeEventListener("change", listener);
			}
		};
	}, [reactHost, toggleFlag]);
	const content = reactHost?.portalStorage.getContent();
	return <>{content}</>; // Don't set key on this fragment
});

export default ReactHost;
